import React, { useRef, useState } from 'react';

import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';
import '@splidejs/react-splide/css';

import { ProductTile } from '@src/stories/ecommerce/Product/ProductTile';
import { ObviyoProductRecommendations } from '@src/stories/blocks/ObviyoRecommendations/ObviyoProductRecommendations';
import CarouselArrows from '@src/stories/elements/CarouselArrows/CarouselArrows';

import { getGlobalBreakpoints } from '@src/hooks/useMediaQuery';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

interface PopularProductsProps {
  title?: string;
  products?: any[];
  className?: string;
  onViewMore?: () => void;
}

export const PopularProducts: React.FC<PopularProductsProps> = ({
  title = 'Popular products',
  products = [],
  settings,
  className = '',
  onViewMore
}) => {
  const { recommendation_id, tile_type = 'default' } = settings;

  const { isMobile } = useResponsiveBreakpoints();
  const globalBreakpoints = getGlobalBreakpoints();
  const { mobile: mobile_max, tablet: tablet_max, desktop: desktop_max } = globalBreakpoints;

  const [hasRecommendations, setHasRecommendations] = useState(true);

  const splideRef = useRef<any>(null);
  const fallbackSplideRef = useRef<{ splide: any } | null>(null);

  const displayProducts = products.length > 0 ? products : [];

  const handlePrevious = () => {
    if (splideRef.current) {
      splideRef.current.splide?.go('<');
    }
  };

  const handleNext = () => {
    if (splideRef.current) {
      splideRef.current.splide?.go('>');
    }
  };

  const splideOptions: Options = {
    type: 'slide',
    perPage: 4,
    padding: {
      right: 100
    },
    perMove: 1,
    gap: 16,
    arrows: false,
    pagination: false,
    breakpoints: {
      [tablet_max]: {
        perPage: 3,
        padding: {
          right: 100
        }
      },
      [mobile_max]: {
        perPage: 2,
        gap: 8,
        padding: {
          right: 0
        }
      }
    }
  };

  const obviyoSettings = {
    ...settings,
    slides_per_page_mobile: 2,
    slide_gap_mobile: 8,
    carousel_padding_right_mobile: 0,
    slides_per_page_tablet: 3,
    slide_gap_tablet: 16,
    carousel_padding_right_tablet: 100,
    slides_per_page: 4,
    slide_gap: 16,
    carousel_padding_right: 100
  };

  return (
    <div className={`w-full grow ${className}`}>
      <div className='flex flex-row items-center w-full justify-between mb-4'>
        <p className={`w-full ${isMobile ? 'b7--bold' : 'b6--bold'}`}>{title}</p>
        <div className='flex w-full justify-end items-center space-x-2'>
          <CarouselArrows
            splideRef={hasRecommendations ? splideRef : fallbackSplideRef}
            buttonClasses='text-[24px]'
          />
        </div>
      </div>

      {recommendation_id && (
        <ObviyoProductRecommendations
          settings={obviyoSettings}
          ref={splideRef}
          onRecommendationsLoaded={(loaded) => setHasRecommendations(loaded)}
        />
      )}

      {!hasRecommendations && (
        <Splide
          options={splideOptions}
          onMounted={(splide) => {
            fallbackSplideRef.current = { splide };
          }}
        >
          {displayProducts.map((product, index) => (
            <SplideSlide key={index}>
              <div className='w-full'>
                <ProductTile
                  product={product}
                  settings={settings}
                  siblings={[]}
                  globals={{}}
                  enableWishlist={true}
                  showTags={true}
                  type={tile_type}
                />
              </div>
            </SplideSlide>
          ))}
        </Splide>
      )}
    </div>
  );
};
